import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import 'firebase/firestore';
import 'firebase/functions';
import 'firebase/storage';
import * as firebaseui from 'firebaseui';
import uuid4 from 'uuid/v4';
import { trackEvent } from '../lib/tracking';
import { CurrentUser } from './CurrentUserModel';
import { Pair, PicturesBatch, UnlockVotesResult, VoteBody } from './types';
import { Routes } from '../constants';

if (!process.env.REACT_APP_FIREBASE_CONFIG || !process.env.REACT_APP_FUNCTIONS_REGION) {
  throw new Error('Firebase config not available');
}
const FIREBASE_CONFIG = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG);
const FUNCTIONS_REGION = process.env.REACT_APP_FUNCTIONS_REGION;

const USERS_COLLECTION_NAME = 'users';
const PICTURES_COLLECTION_NAME = 'pictures';
const INITIAL_VOTES_COLLECTION_NAME = 'initialVotePairs';

export const STORAGE_IMG_PREFIX = '/images/';

/** Names of all https callable firebase functions */
const HTTPS_FUNCTIONS = ['getBatch', 'vote', 'removeAllUserPictures', 'unlockAllUserVotes'] as const;

type FunctionInstances = { [k in typeof HTTPS_FUNCTIONS[number]]: firebase.functions.HttpsCallable };

const increment = firebase.firestore.FieldValue.increment(1);

/**
 * One class to handle all direct firebase calls
 *
 * TODO: consider refactoring into few smaller classes (FirebaseAuth, FirebaseStorage, ...)
 */
export class Firebase {
  auth: firebase.auth.Auth;
  private functions: firebase.functions.Functions;
  private storage: firebase.storage.Storage;
  private firestore: firebase.firestore.Firestore;

  private functionInstances: FunctionInstances;

  get storageRef() {
    return this.storage.ref();
  }

  constructor() {
    // const appInstance = firebase.app();
    const appInstance = firebase.initializeApp(FIREBASE_CONFIG);

    this.auth = appInstance.auth();
    this.functions = appInstance.functions(FUNCTIONS_REGION);
    this.storage = appInstance.storage();
    this.firestore = appInstance.firestore();

    this.users = this.firestore.collection(USERS_COLLECTION_NAME);
    this.pictures = this.firestore.collection(PICTURES_COLLECTION_NAME);
    this.initialVotePairs = this.firestore.collection(
      INITIAL_VOTES_COLLECTION_NAME
    ) as firebase.firestore.CollectionReference<Pair>;

    this.functionInstances = this.generateFunctionsInstances();
  }

  // *** Auth API ***

  getCurrentUserRef = (authId: string) => {
    return this.firestore.doc(`/${this.users.path}/${authId}`);
  };

  loginAnonymously = async () => {
    try {
      await this.auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);

      return this.auth.signInAnonymously();
    } catch (error) {
      console.error(error);
    }
  };

  loginWithFacebook = async () => {
    try {
      await this.auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);

      const facebookProvider = new firebase.auth.FacebookAuthProvider();

      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return firebase.auth().signInWithRedirect(facebookProvider);
      } else {
        return currentUser.linkWithRedirect(facebookProvider);
      }
    } catch (error) {
      console.error(error);
    }
  };

  logout = async () => {
    try {
      trackEvent('logout');
      await this.auth.signOut();
      window.location.reload();
    } catch (err) {
      console.error('Unable to sign out', err);
    }
  };

  resolveSignInFailure = async (error: firebaseui.auth.AuthUIError) => {
    const errorCode = error.code;

    // The firebase.auth.AuthCredential type that was used.
    const credential = error.credential;

    console.warn('signInFailure error:', errorCode);

    if (errorCode === 'auth/credential-already-in-use' || errorCode === 'firebaseui/anonymous-upgrade-merge-conflict') {
      // Sign the old existing user, delete current anonymous user
      console.log('Already signed in before, signing in the old user.');

      const currentUser = this.auth.currentUser;

      await this.auth.signInWithCredential(credential);

      if (currentUser?.isAnonymous) {
        console.log(`Deleting old anonymous user "${currentUser.uid}"`);
        try {
          await currentUser.delete();
        } catch (err) {
          console.log('Could not delete a user', err);
        }
      }

      window.history.replaceState(null, document.title, Routes.landing); // location may be '/auth', we want to go to '/'
      window.location.reload();
    } else {
      return Promise.resolve();
    }
  };

  // *** Database ***

  users: firebase.firestore.CollectionReference;
  pictures: firebase.firestore.CollectionReference;
  initialVotePairs: firebase.firestore.CollectionReference<Pair>;

  uploadPhoto = async (photo: any) => {
    const storageRef = this.storage.ref();
    const newImageRef = storageRef.child(STORAGE_IMG_PREFIX + uuid4());
    await newImageRef.put(photo);
    return newImageRef;
  };

  createPictureRecord = async (url: string, userRef: any) => {
    const ref = await this.pictures.add({
      url: url,
      user: userRef,
      votesCount: 0,
    });

    console.log('picture created', ref);

    return ref;
  };

  addUserPicturesCount = async (userRef: any) => {
    await userRef.update({
      picturesCount: increment,
    });
    console.log('count raised');
  };

  setCurrentUserSawResult = async (userRef: any) => {
    return userRef.update({
      onboarding_sawResultsReceivedPrompt: true,
    } as Partial<CurrentUser>);
  };

  getInitialVotePairs = async (count: number) => {
    const result = await this.initialVotePairs.limit(count).get();
    return result.docs.map((doc) => doc.data());
  };

  // *** Functions API ***

  getBatch = async () => {
    const { data } = await this.functionInstances.getBatch();
    return data as PicturesBatch;
  };

  sendVote = async (payload: VoteBody) => {
    return this.functionInstances.vote(payload);
  };

  removeAllUserPictures = async () => {
    await this.functionInstances.removeAllUserPictures();
  };

  unlockAllUserVotes = async () => {
    const { data } = await this.functionInstances.unlockAllUserVotes();
    return data.data as UnlockVotesResult;
  };

  private generateFunctionsInstances = () => {
    /** Generate callable firebase functions instances */
    return HTTPS_FUNCTIONS.reduce((acc, name) => {
      acc[name] = this.getFunctionInstance(name);
      return acc;
    }, {} as FunctionInstances);
  };

  private getFunctionInstance = (name: string) => {
    return this.functions.httpsCallable(name);
  };
}
